import React from 'react'
import IconButton from '@material-ui/core/IconButton' // MUI 4 import
import PropTypes from 'prop-types'
import styled from 'styled-components' // Import styled-components
import { useTheme, alpha } from '@material-ui/core/styles' // Import alpha helper for opacity
import {
  getBorderRadius,
  getIconButtonPadding,
  getColorVariant,
} from 'src/components/core/functions' // Import getBorderRadius function

// Define the base styles for the button using styled-components
const BaseButton = styled(IconButton)`
  box-sizing: border-box;
  box-shadow: none;
  text-transform: none;
  font-family: 'Inter';
  border-radius: ${({ size }) => getBorderRadius(size)};
  padding: ${({ size }) => getIconButtonPadding(size)};
  letter-spacing: 0.2px;
  white-space: nowrap;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'fit-content')};
  ${({ size }) => {
    let dimension
    switch (size) {
      case 'small':
        dimension = '32px'
        break
      case 'large':
        dimension = '40px'
        break
      case 'medium':
      default:
        dimension = '36px'
    }
    return `
      min-width: ${dimension};
      width: ${dimension};
      height: ${dimension};
    `
  }}
  &:hover {
    box-shadow: none;
  }
`

// Define variant-specific styles for contained buttons
const ContainedIconButton = styled(BaseButton)`
  color: ${({ theme, color, disabled }) =>
    disabled
      ? theme.palette.action.disabled
      : getColorVariant(theme, color).opposite};
  background-color: ${({ theme, color, disabled }) =>
    disabled
      ? theme.palette.action.disabledBackground
      : getColorVariant(theme, color).main};
  &:hover {
    background-color: ${({ theme, color, disabled }) =>
      disabled
        ? theme.palette.action.disabledBackground
        : getColorVariant(theme, color).main};
    box-shadow: ${({ disabled }) =>
      disabled ? 'none' : '0px 2px 4px 0px rgba(145, 158, 171, 0.32)'};
  }
`

// Define variant-specific styles for outlined buttons
const OutlinedIconButton = styled(BaseButton)`
  color: ${({ theme, color, disabled }) =>
    disabled
      ? theme.palette.action.disabled
      : getColorVariant(theme, color).main};
  border: 1px solid
    ${({ theme, color, disabled }) =>
      disabled
        ? theme.palette.action.disabledBackground
        : getColorVariant(theme, color).main};
  padding: ${({ size }) => {
    const basePadding = parseInt(getIconButtonPadding(size))
    return `${basePadding - 1}px` // Subtract 1px to compensate for border
  }};
  &:hover {
    background-color: ${({ theme, color, disabled }) =>
      disabled ? 'none' : alpha(getColorVariant(theme, color).main, 0.1)};
    border: 1px solid
      ${({ theme, color, disabled }) =>
        disabled && theme.palette.action.disabledBackground};
    color: ${({ theme, color, disabled }) =>
      disabled
        ? theme.palette.action.disabled
        : getColorVariant(theme, color).main};
  }
`

// Define variant-specific styles for text buttons
const TextIconButton = styled(BaseButton)`
  color: ${({ theme, color, disabled }) =>
    disabled
      ? theme.palette.action.disabled
      : getColorVariant(theme, color).main};
  &:hover {
    background-color: ${({ theme, color, disabled }) =>
      disabled ? 'none' : alpha(getColorVariant(theme, color).main, 0.1)};
  }
`

const CoreIconButton = React.forwardRef(
  (
    {
      variant = 'contained',
      color = 'primary',
      size = 'medium',
      fullWidth = false,
      disabled = false,
      onClick,
      children,
      ...props
    },
    ref,
  ) => {
    // Add ref parameter
    const theme = useTheme()

    const ButtonVariant =
      {
        contained: ContainedIconButton,
        outlined: OutlinedIconButton,
        text: TextIconButton,
      }[variant] || ContainedIconButton

    return (
      <ButtonVariant
        ref={ref} // Forward the ref
        theme={theme}
        color={color}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        {...props} // This will now properly forward all event handlers
      >
        {children}
      </ButtonVariant>
    )
  },
)

// Prop validation
CoreIconButton.propTypes = {
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'error',
    'disabled',
    'info',
    'success',
    'warning',
    'white',
    'dark',
    'gray',
    'inherit',
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fullWidth: PropTypes.bool, // Add prop validation for fullWidth
  disabled: PropTypes.bool, // Add prop validation for disabled
  onClick: PropTypes.func, // Validate the onClick prop
  children: PropTypes.node.isRequired,
}

export default CoreIconButton
